<script setup lang="ts">
import { HttpStatusCode } from "axios";
import { storeToRefs } from "pinia";
import { eventBus } from "~/core/services/eventBus";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useDeleteSector } from "~/modules/sector/composables/useDeleteSector";
import { useFetchSectors } from "~/modules/sector/composables/useFetchSectors";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import { findSectorParentInSectorTree } from "~/modules/sector/utils/findSectorParentInSectorTree";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";

const props = defineProps<{
  isVisible: boolean;
  sector?: Sector;
}>();
const emit = defineEmits(["change", "close"]);

const { t } = useI18n();

const { sectors } = storeToRefs(useSectorStore());

const { currentOrganization } = storeToRefs(useOrganizationStore());

const { fetchSectors } = useFetchSectors(currentOrganization.value?.code);

const { isLoading, error, deleteSector } = useDeleteSector(
  currentOrganization.value?.code,
);

const [removeDeniedDialogVisible, toggleRemoveDeniedDialogVisible] =
  useToggle();

/**
 * Remove a sector
 */
async function onRemoveSector() {
  if (props.sector?.code) {
    await deleteSector(props.sector.code);

    if (error.value) {
      const removeDenied =
        error.value.status === HttpStatusCode.Forbidden &&
        error.value.codes?.find(
          (code) => code.code === "sector_delete_forbidden",
        );

      if (removeDenied) {
        emit("close");
        toggleRemoveDeniedDialogVisible(true);
      } else {
        error.value.notify(NotificationKey.removeError);
      }
    } else {
      const parentSectorCode = sectors.value
        ? findSectorParentInSectorTree(sectors.value, props.sector.code)?.code
        : undefined;

      const sectorCode = props.sector.code;

      emit("change");

      showNotificationSuccess(t(NotificationKey.removeSuccess));

      await fetchSectors(true);

      eventBus.emit("sector", {
        parentSectorCode,
        sectorCode,
      });
    }
  }
}
</script>

<template>
  <confirm-modal
    :is-dialog-visible="isVisible"
    :title="t('sectors.modals.remove.title', { sectorName: sector?.name })"
    :description="t('sectors.modals.remove.description')"
    :disabled-confirm="isLoading"
    :is-loading="isLoading"
    :icon="ILxcLightTrash2"
    icon-color-theme="error"
    color-theme="danger"
    @cancel="emit('close')"
    @confirm="onRemoveSector"
  />
  <sector-remove-denied
    :is-visible="removeDeniedDialogVisible"
    @close="toggleRemoveDeniedDialogVisible(false)"
  />
</template>
